import { useEffect, useMemo, useState } from 'react';
import { createStore, useStore } from 'zustand';
import { debounce } from '@optimus-web/common';

interface ScrollRestorationStore {
    scrollRestoration: Record<string, { scrollTop: number; scrollLeft: number }>;
    setScrollRestoration: (key: string, scroll: { scrollTop: number; scrollLeft: number }) => void;

    shouldRestoreScroll: boolean;
    setShouldRestoreScroll: (shouldRestore: boolean) => void;
}

const scrollRestorationStore = createStore<ScrollRestorationStore>((set) => ({
    scrollRestoration: {},
    setScrollRestoration: (key, scroll) =>
        set((store) => ({ scrollRestoration: { ...store.scrollRestoration, [key]: scroll } })),

    shouldRestoreScroll: false,
    setShouldRestoreScroll: (shouldRestoreScroll) => set({ shouldRestoreScroll }),
}));

interface ScrollRestorationOptions {
    debounceTime?: number;
    getElement: () => HTMLElement | null | undefined;
}

export function useScrollRestoration(
    key: string | undefined,
    { debounceTime = 100, getElement }: ScrollRestorationOptions
) {
    const { scrollRestoration, setScrollRestoration, shouldRestoreScroll } = useStore(scrollRestorationStore);
    const [running, setRunning] = useState(false);

    const currentScrollRestoration = key == null ? undefined : scrollRestoration[key];
    const hasRestoration = key != null && key in scrollRestoration;

    // add event listener
    useEffect(() => {
        const element = getElement();
        if (!element || !running || !key) return;

        const handleScroll = debounce(() => {
            setScrollRestoration(key, {
                scrollTop: element.scrollTop,
                scrollLeft: element.scrollLeft,
            });
        }, debounceTime);

        element.addEventListener('scroll', handleScroll);
        return () => {
            element.removeEventListener('scroll', handleScroll);
        };
    }, [debounceTime, getElement, key, running, setScrollRestoration]);

    // restore or initialize scroll
    useEffect(() => {
        const element = getElement();
        if (!element || !running || !key || !shouldRestoreScroll) return;

        if (hasRestoration && currentScrollRestoration) {
            window.setTimeout(
                () => element.scrollTo(currentScrollRestoration.scrollLeft, currentScrollRestoration.scrollTop),
                debounceTime
            );
            setShouldRestoreScroll(false);
        } else {
            setScrollRestoration(key, {
                scrollTop: element.scrollTop,
                scrollLeft: element.scrollLeft,
            });
        }
    }, [
        getElement,
        hasRestoration,
        currentScrollRestoration,
        setScrollRestoration,
        key,
        running,
        shouldRestoreScroll,
        debounceTime,
    ]);

    return useMemo(
        () => ({
            start: () => {
                setRunning(true);
            },
            stop: () => {
                setRunning(false);
            },
        }),
        []
    );
}

export const { setShouldRestoreScroll } = scrollRestorationStore.getState();
